// src/models/WorkflowModel.ts
import { WorkflowRegistry } from '../enum/workflow';
import { MeshTreeData } from './MeshTreeData';
import * as LayoutUtility from '@/utils/layout';

/* eslint-disable @typescript-eslint/no-explicit-any*/

export class WorkflowModel<TStepName extends string> {
  private _active: number;
  public localeIdentifier: string;
  public Progress: Record<string, TStepName>;
  public readonly stepCount: number;
  public finished = false;
  public navigationStart = false;
  public totalPriceArray: Map<string, number[]>;

  public OnClickNextAction = this.changeActiveStep;

  public currentStepName: TStepName;

  private _progressOrder: TStepName[];
  private _getStepNameFromIndex: (index: number) => TStepName | undefined;

  constructor(
    localeIdentifier: string,
    progressEnum: Record<string, TStepName>,
    productDetailsCallback?: (model?: any) => void,
  ) {
    const definition = WorkflowRegistry.get(progressEnum);

    if (!definition) {
      throw new Error(`WorkflowModel: No definition found in registry for provided enum.`);
    }

    this._progressOrder = definition.order;
    this._getStepNameFromIndex = definition.getFromIndex;

    this.localeIdentifier = localeIdentifier;
    this.Progress = progressEnum;
    this.stepCount = this._progressOrder.length;
    this.finished = false;
    this.totalPriceArray = new Map<string, number[]>();

    this._active = 0;
    this.currentStepName = this._getStepNameFromIndex(this._active) || this._progressOrder[0];
  }

  public get active(): number {
    return this._active;
  }

  private set active(newActiveIndex: number) {
    if (newActiveIndex < 0) newActiveIndex = 0;
    if (newActiveIndex >= this.stepCount) newActiveIndex = this.stepCount - 1;

    if (this._active === newActiveIndex) {
      return;
    }

    this._active = newActiveIndex;
    this.currentStepName = this._getStepNameFromIndex(this._active) || this._progressOrder[0];

    this.navigationStart = true;

    LayoutUtility.refresh();
  }

  public setStep(targetIndex: number): void {
    if (targetIndex < 0) {
      targetIndex = 0;
    } else if (targetIndex >= this.stepCount) {
      this.finished = true;
      return;
    }

    if (this._active === targetIndex) {
      return;
    }

    this.active = targetIndex;
    this.currentStepName = this._getStepNameFromIndex(this._active) || this._progressOrder[0];

    this.navigationStart = true;

    if (this.finished) {
      this.finished = false;
    }

    LayoutUtility.refresh();
  }

  public changeActiveStep(difference: number): void {
    this.setStep(this.active + difference);
  }

  private getModelForProductDetails(): any {
    return {};
  }

  public isNextStepPossible(): boolean {
    return this.active < this.stepCount && this.currentStepName !== 'productType';
  }

  public activeProgressName(): TStepName {
    return this.currentStepName;
  }

  public isPrevStepPossible(): boolean {
    return this.active > 0;
  }

  public isLastStep(): boolean {
    return this.active == this.stepCount - 1;
  }

  public initTotalPrice(treeData: MeshTreeData) {
    this.totalPriceArray.clear();

    const prices: number[] = [];
    for (let i = 0; i < this.stepCount; i++) {
      prices.push(0.0);
    }
    this.totalPriceArray.set('product', prices);

    for (const mesh of treeData.treeData) {
      const prices: number[] = [];
      for (let i = 0; i < this.stepCount; i++) {
        prices.push(0.0);
      }
      this.totalPriceArray.set(mesh.id!, prices);
    }
  }

  public setStepPrice(meshTreeDataItemId: string, stepIndex: number, price: number): void {
    const stepArray = this.totalPriceArray.get(meshTreeDataItemId);

    if (stepArray) {
      if (stepIndex >= 0 && stepIndex < stepArray.length) {
        stepArray[stepIndex] = price;
        this.totalPriceArray.set(meshTreeDataItemId, stepArray);
      } else {
        console.warn(`WorkflowModel: Step index ${stepIndex} out of bounds for setStepPrice.`);
      }
    } else {
      console.warn(`WorkflowModel: Mesh data for ${meshTreeDataItemId} not found.`);
    }
  }

  get totalPrice(): number {
    const ZIPPER_PRICE = 65;
    const SPECIAL_EMBOSSED_PRICE = 145;
    let sum = 0.0;
    let twoEmbossingColors = false;

    for (const component of this.totalPriceArray) {
      for (const price of component[1]) {
        if (price !== ZIPPER_PRICE) {
          if (price === SPECIAL_EMBOSSED_PRICE && !twoEmbossingColors) {
            sum += price;
            twoEmbossingColors = true;
          } else if (price !== SPECIAL_EMBOSSED_PRICE) {
            sum += price;
          }
        }
      }
    }
    return sum;
  }
}